<template>
  <div class="kpi-card-alternative">
    <!-- Meta Information Header -->
    <div class="kpi-meta">
      <span class="meta-item">{{ control.label }}</span>
      <span class="meta-divider"></span>
      <span class="meta-item">n={{ getCount }}</span>
    </div>

    <!-- KPI Type Tabs -->
    <div class="kpi-tabs">
      <div class="border-radius-sm-top kpi-tab">
        <div class="tab-label">{{ control.kpiGroupNameShort || item?.KpiGroupCode || '&nbsp;' }}</div>
      </div>
    </div>
    <div class="kpi-tabs">
      <div class="border-radius-sm-bottom kpi-tab active">
        <div class="tab-label">{{ control.kpiNameShort || '&nbsp;' }}</div>
      </div>
    </div>

    <!-- Main Score Display -->
    <div class="kpi-score-container">
      <div class="score-main">
        <div class="score-value-wrapper">
          <div class="score-number-group">
            <span class="score-value" :class="{ 'score-value-na': getResult() === $t('Webportal.NotAvailable')}">{{ getResult() }}</span>
          </div>
          <div class="score-target" :class="deltaClass">
            <span class="target-label">{{ $t('Webportal.Target') }}</span>
            <span class="target-value" :style="{ color: targetColor }">{{ this.targetValue }}</span>
          </div>
        </div>
        <span class="score-unit" :class="{ 'score-unit-na': getResult() === $t('Webportal.NotAvailable')}">%</span>
      </div>

      <!-- Badges & Previous Wave Info -->
      <div class="kpi-info">
        <div v-if="delta !== null" class="delta-badge" :class="deltaClass">
          <span :style="{ 'font-size': getTrend() === '\u00A0-' ? '16px' : '12px' }">{{ getTrend() }}</span>
        </div>
        <div class="previous-wave" v-if="getSecondLastResult() !== null">
          <span class="prev-label">{{ $t('Webportal.Prewave') }}</span>
          <span class="prev-value">{{ getSecondLastResult() }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useKpiHelpers } from '@/composables/kpi-helpers';
import { mapGetters } from 'vuex';

export default {
  name: 'KpiCardAlternative',

  props: {
    boardData:      { type: Object, default: () => ({}) },
    control:        { type: Object, required: true },
    waveData:       { type: Object,  required: true }
  },

  watch: {
    boardData: {
      handler() { this.getKpi(this.boardData, this.control); },
      immediate: true
    }
  },

  data() {
    return {
      item: null,
      kpi: null,
      kpiGroup: null
    };
  },

  setup() {
    const KPI = useKpiHelpers();
    return { KPI };
  },

  computed: {
    ...mapGetters('clientData', [ 'indices' ]),

    targetValue() {
      let index = this.indices.find(i => i.indexId === this.control.indexId);
      return index?.thresholds[0].min || this.$t('Webportal.NotAvailable');
    },

    targetColor() {
      const indexId = this.control?.indexId;
      if (indexId == null || indexId < 0) return null;
      const index = this.indices?.find(idx => idx.indexId === indexId);
      if (!index?.thresholds) return null;
      const thre = index.thresholds[0];
      return thre?.color || null;
    },

    delta() {
      return this.getTrend();
    },

    deltaClass() {
      if (this.delta === null) return '';
      if (this.delta > 0) return 'positive';
      if (this.delta < 0) return 'negative';
      return 'neutral';
    },
    getCount() {
      const wave = this.item?.SurveyWave.find(x => x.Name === this.waveData.Waves[0].Name);
      if (!wave) return this.$t('Webportal.NotAvailable');
      return wave?.SurveyInstanceCount || this.$t('Webportal.NotAvailable');
    },


  },

  methods: {

    getKpi(boardData, control) {
      if (!boardData || !control) return null;
      this.kpi = boardData.KPI ? boardData.KPI.find(k => k.KpiCode === control.kpiCode) : null;
      this.kpiGroup = boardData.KPIGroup ? boardData.KPIGroup.find(g => g.KpiCode === control.kpiGroupCode) : null;
      this.item = control.type === 'kpi' ? this.kpi : this.kpiGroup;
    },

    getTrend() {
      const waves = this.item?.SurveyWave;
      if (!waves || waves.length < 2) return '\u00A0' + '-';

      const lastResult = waves[waves.length - 1]?.Result;
      const secondLastResult = waves[waves.length - 2]?.Result;

      if (lastResult == null || secondLastResult == null) return '\u00A0' + '-';

      const trend = lastResult - secondLastResult;
      if (trend === 0) return '\u00A0' + '0';

      const sign = trend > 0 ? '+' : '';
      return `${sign}${trend}`;
    },

    getResult() {
      const wave = this.item?.SurveyWave.find(x => x.Name === this.waveData.Waves[0].Name);
      if (!wave) return this.$t('Webportal.NotAvailable');
      return wave?.Result;
    },

    getSecondLastResult() {
      const wave = this.item?.SurveyWave.find(x => x.Name === this.waveData.Waves[1].Name);
      if (!wave) return this.$t('Webportal.NotAvailable');
      return wave?.Result;
    }
  }
};
</script>

<style lang="scss" scoped>
.kpi-card-alternative {
  background-color: var(--target-card-bg);
  border: 1px solid var(--color-border);
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  // transform: skewX(var(--target-skew-angle-inverse));
  position: relative;
  border-radius: var(--bs-border-radius-sm);
  border-color: var(--color-border);

  > * {
    // transform: skewX(var(--target-skew-angle));
  }

  > *:first-child {
    margin-bottom: 0.75rem;
  }

  > *:nth-last-child(2) {
    margin-bottom: 0.75rem;
  }

}

 .border-radius-sm-top {
   border-top-left-radius: var(--bs-border-radius-sm);
   border-top-right-radius: var(--bs-border-radius-sm);
 }
.border-radius-sm-bottom {
  border-bottom-left-radius: var(--bs-border-radius-sm);
  border-bottom-right-radius: var(--bs-border-radius-sm);
}
.kpi-meta {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
    color: var(--color-muted-foreground);
  flex-wrap: wrap;
  height: 16px;
}

.meta-item {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 10px;
  line-height: 12px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
}

.meta-divider {
  width: 1px;
  height: 16px;
  border: 1px solid var(--color-accent);
  margin-left: auto;
  // transform: skewX(calc(1 * var(--target-skew-angle-inverse)));
}

.kpi-tabs {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  width: 100%;
  padding: 0 0 0 0.5px;
  // transform: skewX(var(--target-skew-angle-inverse));
}

.kpi-tab {
  flex: 1;
  border: 1px solid  var(--color-border);
  padding: 4px 8px;

  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 10px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  // transform: skewX(calc(1 * var(--target-skew-angle-inverse)));
  width: 100%;

  &.active {
    background: var(--target-card-header-bg);
  }
}

.tab-label {
  // transform: skewX(calc(1 * var(--target-skew-angle)));
  text-align: left;
  width: 100%;
}

.kpi-score-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.score-main {
  display: flex;
  align-items: flex-end;
  gap: 0px;
  font-weight: 500;
}

.score-value-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
}

.score-number-group {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.score-value {
  font-size: 48px;
  line-height: 48px;
  color: var(--color-foreground);
  letter-spacing: 0.3955px;
  white-space: nowrap;
}
.score-value-na {
  color: var(--color-muted-number);
}

.score-unit {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 24px;
  line-height: 60px;
  color: var(--color-muted-foreground);
  letter-spacing: 0.3955px;
  white-space: nowrap;
}
.score-unit-na {
  display: none;
}
.score-target {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 0 4px;
  width: 100%;
}

.target-label {
  font-size: 12px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.target-value {
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  color: var(--color-muted-foreground);
}

.kpi-info {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 64px;
  padding-top: 4px;
}

.delta-badge {
  border: 1px solid var(--color-border);
  padding: 2px 12px;
  // transform: skewX(var(--target-skew-angle-inverse));
  border-radius: var(--bs-border-radius-sm);
  min-width: 2.5rem;

  span {
    // transform: skewX(var(--target-skew-angle));
    display: inline-block;
    font-family: 'BMWTypeNext Pro', sans-serif;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
  }

  &.positive {
    background: var(--default-status-bg-green);
    border-color: var(--default-status-bg-green);

    span {
      color: var(--default-status-green);
    }
  }

  &.negative {
    background: var(--default-status-bg-red);
    border-color: var(--default-status-bg-red);

    span {
      color: var(--default-status-red);
    }
  }

  &.neutral {
    span {
      color: var(--default-status-indifferent);
    }
  }
}

:root,
[data-bs-theme=light] {
  .delta-badge.neutral {
    background: rgba(0, 0, 0, 0.05);
    border-color: rgba(0, 0, 0, 0.1);
  }
}

[data-bs-theme=dark] {
  .delta-badge.neutral {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.previous-wave {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
}

.prev-label {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-muted-foreground);
  white-space: nowrap;
}

.prev-value {
  font-family: 'BMWTypeNext Pro', sans-serif;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-foreground);
  white-space: nowrap;
}

</style>
